import React, { useState, useEffect } from 'react';
import {
    Box,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Typography,
    Checkbox,
} from '@mui/material';
import { httpGet, httpPost } from '../../HttpRequestHandling/httpRequestHandler';
import EnhancedDataGrid from '../Common/EnhancedDataGrid';
import { toast } from 'react-toastify';
import { getDisplayName } from '../Common/Utilities';

export default function GroupRules(props) {
    const [selectedGroup, setSelectedGroup] = useState('');
    const [originalGroupRules, setOriginalGroupRules] = useState([]);
    const [userGroups, setUserGroups] = useState([]);
    const [groupRules, setGroupRules] = useState([]);
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        props.inProgress(true);
        httpGet('/api/userroles/')
            .then((response) => {
                const data = response.data;
                if (!Array.isArray(data)) {
                    console.error('Expected array from /api/userroles/, got:', data);
                    return;
                }
                setUserGroups(data);
                if (data.length > 0) {
                    const firstGroup = data[0];
                    setSelectedGroup(firstGroup.userRoleName);
                    fetchGroupRules(firstGroup.companyCode, firstGroup.userRoleId);
                }
            })
            .catch((error) => {
                console.error('Error loading user roles:', error);
                toast.error("An error occurred while fetching the User Roles");
                props.inProgress(false);
            })
            .finally(() => props.inProgress(false));
            
    }, []);

    const handleGroupChange = (e) => {
        const selectedRoleName = e.target.value;
        setSelectedGroup(selectedRoleName);
        const selectedGroupData = userGroups.find(role => role.userRoleName === selectedRoleName);
        if (selectedGroupData) {
            fetchGroupRules(selectedGroupData.companyCode, selectedGroupData.userRoleId);
        }
    };


    const fetchGroupRules = (companyCode, userRoleId) => {
        props.inProgress(true);
        httpGet(`/api/usergrouprules/${companyCode}/${userRoleId}`)
            .then((res) => {
                const rulesWithBooleans = res.data.map(rule => ({
                    ...rule,
                    editable: rule.editable === "1" ? "1" : "0" 
                }));
                setGroupRules(rulesWithBooleans);
                setOriginalGroupRules(rulesWithBooleans); 
            })
            .catch((err) => {
                console.error('Error fetching group rules:', err);
                toast.error("An error occurred while fetching User Group Rule(s)");
            })
            .finally(() => props.inProgress(false));
    };

    const handleCheckboxChange = (index) => {
        const updated = [...groupRules];
        const current = updated[index];

        updated[index] = {
            ...current,
            editable: current.editable === "1" ? "0" : "1"
        };
        setDirty(true);
        setGroupRules(updated);
    };


    const handleReset = () => {
        setGroupRules([...originalGroupRules]);
        setDirty(false);
       
    };

    const handleSave = () => {

        const getRuleKey = (rule) => `${rule.Model_def_id}_${rule.path}`;

        const originalMap = new Map(
            originalGroupRules.map(rule => [getRuleKey(rule), rule])
        );

        const updatedOnly = groupRules.filter(rule => {
            const key = getRuleKey(rule);
            const original = originalMap.get(key);
            return original && rule.editable !== original.editable;
        });

        if (updatedOnly.length === 0) {
            return;
        }

        props.inProgress(true);
        console.log("Posting:", updatedOnly);


        httpPost('/api/usergrouprules', updatedOnly)
            .then((res) => {
                const updatedRules = res.data.map(rule => ({
                    ...rule,
                    editable: rule.editable === "1" ? "1" : "0"
                }));
                setGroupRules(updatedRules);
                setOriginalGroupRules(updatedRules);
                setDirty(false);
                toast.success("Group Rule(s) updated successfully");
            })
            .catch(err => {
                console.error("Save error:", err);
                toast.error('An error occurred while saving the User Group Rule(s)');
            })
            .finally(() => {
                props.inProgress(false);
            });
    };

    const modelDefTitles = {
        1: "Media",
        2: "Production",
        3: "Broadcast"
    };
    const getDynamicLabel = (label) => {
        if (label === 'User Status') {
            return getDisplayName('User Status');
        }
        return label;
    };


    const transformToTreeData = (rules) => {
        const grouped = {};

        rules.forEach((rule, idx) => {
            const groupId = rule.Model_def_id;
            const groupLabel = modelDefTitles[groupId] || `Group ${groupId}`;

            if (!grouped[groupId]) {
                grouped[groupId] = {
                    label: groupLabel,
                    children: []
                };
            }

            grouped[groupId].children.push({
                ...rule,
                id: `${groupId}_${rule.path}_${idx}`,
                groupLabel,
                description: getDynamicLabel(rule.label),
                index: idx
            });
        });

        const rows = [];
        Object.values(grouped).forEach((group, i) => {
            rows.push({
                id: `group-header-${i}`,
                isGroupHeader: true,
                description: group.label,
            });

            rows.push(...group.children);
        });

        return rows;
    };

    const columns = [
        {
            field: 'description',
            headerName: 'Rule Description',
            flex: 1,
            minWidth: 400,
            maxWidth: 800,
            renderCell: (params) => {
                if (params.row.isGroupHeader) {
                    return (
                        <Typography
                            variant="h6"
                            sx={{
                                py: 1,
                                fontSize: '15px'
                            }}
                        >
                            {params.value}
                        </Typography>
                    );
                }
                return (
                    <Typography variant="body2" sx={{pl:2} }>
                        {params.value}
                    </Typography>
                );
            }
        },
        {
            field: 'type',
            headerName: 'Type',
            width: 150,
            renderCell: (params) =>
                !params.row.isGroupHeader && (
                    <Typography variant="body2">
                        {params.value}
                    </Typography>
                ),
        },
        {
            field: 'editable',
            headerName: 'Editable',
            width: 120,
            sortable: false,
            renderCell: (params) =>
                !params.row.isGroupHeader && (
                    <Checkbox
                        checked={params.value === "1"}
                        onChange={() => handleCheckboxChange(params.row.index)}
                        size="small"
                        sx={{ p: 0.5 }}
                    />
                )
        }
    ];


    const getRowClassName = (params) => {
        return params.row.isGroupHeader ? 'group-header-row' : 'leaf-row';
    };

    return (
        <Box sx={{ px: 1, pt: 1, pb: 3 }}>
            <h1 style={{ marginTop: 10 }}>Group Rules</h1>

            <FormControl fullWidth sx={{ maxWidth: 300, mb: 3, mt: 3 }}>
                <InputLabel>User Group</InputLabel>
                <Select
                    value={selectedGroup}
                    label="User Group"
                    onChange={handleGroupChange}
                >
                    {userGroups.map((g) => (
                        <MenuItem key={g.userRoleId} value={g.userRoleName}>
                            {g.userRoleName}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>


            <EnhancedDataGrid
                rows={transformToTreeData(groupRules)}
                columns={columns}
                getRowId={(row) => row.id}
                disableSelectionOnClick
                hidePagination={true}
                nofilter={true}
                getRowClassName={getRowClassName}
                autoHeight
                sx={{
                    mt: 2,
                    '& .MuiDataGrid-columnHeaderTitle': {
                        fontSize: '16px',
                    },
                    '& .MuiCheckbox-root': {
                        padding: '0 8px',
                    },
                    '& .MuiDataGrid-row.group-header-row .MuiDataGrid-cell': {
                        fontSize: '15px',
                        fontWeight: 500,
                    },
                    '& .MuiDataGrid-row.leaf-row .MuiDataGrid-cell': {
                        fontSize: '14px',
                    }
                }}
            />

         
            <Box sx={{
                position: 'fixed',
                bottom: 20,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 1000,
                display: 'flex',
                gap: 2
            }}>
                <button className="btn-grey" onClick={handleReset} disabled={!dirty}>Reset</button>
                <button className="btn-blue" onClick={handleSave} disabled={!dirty}>Save</button>

            </Box>
        </Box>
    );
}
